import { Select } from '@customerio/pluma-components/react';
import {
  COLLECTION_ATTRIBUTE_CONDITION_TYPE,
  AttributeRow as SharedAttributeRow,
  attributeConditionOperatorGroupsForField,
  collectionFilterBuilder,
} from '@/features/dryad';

type CollectionAttributeRowProps = {
  id: string;
};

function uniqueOptions(names: string[]) {
  return [...new Set(names)].map((name) => ({ value: name, label: name }));
}

// Collection filter values have no suggestions yet: the value endpoints behind
// them are people-attribute scoped, and there is no collection-scoped source to
// query. The shared row keeps the options/loading props so wiring one up later
// is a change of argument, not of shape.
const EMPTY_VALUE_OPTIONS: { value: string; label: string }[] = [];

/**
 * The collections surface's attribute row: the shared row, plus a name picker
 * over the collection's own schema. Unlike people's it offers `withCreate`,
 * because a column the stored schema hasn't seen yet is still filterable.
 */
export function CollectionAttributeRow({ id }: CollectionAttributeRowProps) {
  const meta = collectionFilterBuilder.useMeta();

  return (
    <SharedAttributeRow
      id={id}
      conditionType={COLLECTION_ATTRIBUTE_CONDITION_TYPE}
      operatorGroups={attributeConditionOperatorGroupsForField}
      isDisabled={meta.isDisabled === true}
      alignBlock="center"
      valueOptions={EMPTY_VALUE_OPTIONS}
      // This surface renders no inline field error, so there is no untouched
      // state for the picker to hide one behind.
      isFieldTouched
      onFieldTouched={() => {}}
      renderNamePicker={({ field, isDisabled, onFieldChange }) => (
        <Select
          ariaLabel="Collection attribute"
          value={field}
          options={uniqueOptions([...meta.schema, ...(field === '' ? [] : [field])])}
          placeholder="Select an attribute"
          searchPlaceholder="Search attributes"
          isSearchable
          withCreate
          onCreate={onFieldChange}
          onChange={(value) => onFieldChange(value ?? '')}
          size="sm"
          isDisabled={isDisabled}
        />
      )}
    />
  );
}
